// Ultimate Card Game (UCG) - Matchmaking Mini-Brawler
//
// Cosmetic-only 1v1 skirmish rendered inside the online-lobby "waiting for
// opponent" screen, replacing the static "QUEUE #1" placeholder. Two fake
// combatants trade shots until one dies, then a replacement runs in - purely
// decorative, so it deliberately does NOT go through Battlefield.spawnRaw()/
// new Unit() (see CLAUDE.md convention 2: that contract is for real
// simulation units with an id/battlefield wired in for gameplay logic).
// Instead each fighter is a plain object carrying only the fields
// unitMixin.renderUnit() (js/engine/render/units.js) actually reads, so the
// authentic per-unit-type walk-cycle/weapon-pose animation renders for free
// without reimplementing it here.

import { getCardByUnitType } from '../data/cards.js';
import { ParticleEngine } from '../engine/particles.js';
import { unitMixin } from '../engine/render/units.js';

const WESTERN_POOL = ['Marine', 'DevilPatrol', 'TeslaWalker'];
const EASTERN_POOL = ['DefectiveLoaf', 'ApostateLoaf'];

const CANVAS_WIDTH = 380;
const CANVAS_HEIGHT = 92;
const LANE_Y = 56;
const RADIUS = 17; // all whitelisted units are tier 1 -> same radius (js/engine/unit.js TIER_RADIUS)
const SPAWN_MARGIN = 26;
// Deliberately uniform across every unit instead of each card's real range
// value, so the walk-in/engage distance is identical regardless of matchup.
const ENGAGE_DISTANCE = 66;
const WALK_SPEED = 95; // canvas px/sec, independent of the real match's speed scale
const RESPAWN_DELAY = 0.6;
const HIT_FLASH_DURATION = 0.08;
const ATTACK_POSE_DURATION = 0.25;

const TEAM_COLOR = { western: '#00f0ff', eastern: '#ff003c' };

// ParticleEngine.render() culls beams, special effects and particles against the visible world
// range. Without explicit bounds it asks the MAIN game camera (window.tcgGame.viewport), which
// knows nothing about this canvas: while that camera is parked away from x = 0..CANVAS_WIDTH (app
// start, before the background camera has panned there) every tracer, muzzle flash and spark of the
// lobby brawler is culled and only the (not culled) units animate. The brawler's own canvas is the
// only "screen" it has, so it says so.
const VIEW_BOUNDS = Object.freeze({
  left: 0,
  right: CANVAS_WIDTH,
  top: 0,
  bottom: CANVAS_HEIGHT,
  width: CANVAS_WIDTH,
  height: CANVAS_HEIGHT
});

export class MatchmakingBrawler {
  constructor(canvasEl, killBadgeEl) {
    this.canvas = canvasEl;
    this.ctx = canvasEl.getContext('2d');
    this.killBadgeEl = killBadgeEl || null;

    this.particles = new ParticleEngine();
    this.fighters = { western: null, eastern: null };
    this._respawnTimer = { western: 0, eastern: 0 };
    this._spawnCounter = 0;
    this.kills = 0;

    this._rendererStub = { quality: 'high' };
    this._battlefieldStub = {
      laneY: LANE_Y,
      laneHeight: 40,
      isInRange: (unit) => !!unit._engaged
    };

    this._running = false;
    this._animFrameId = null;
    this._lastTime = 0;
    this._frame = this._frame.bind(this);

    this._applyCanvasSize();
  }

  _applyCanvasSize() {
    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = CANVAS_WIDTH * dpr;
    this.canvas.height = CANVAS_HEIGHT * dpr;
    this.canvas.style.width = CANVAS_WIDTH + 'px';
    this.canvas.style.height = CANVAS_HEIGHT + 'px';
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  start() {
    if (this._running) return;
    this._running = true;
    this.kills = 0;
    this._updateKillBadge();
    this.fighters.western = this._spawnFighter('western');
    this.fighters.eastern = this._spawnFighter('eastern');
    this._respawnTimer.western = 0;
    this._respawnTimer.eastern = 0;
    this._lastTime = performance.now();
    this._animFrameId = requestAnimationFrame(this._frame);
  }

  stop() {
    this._running = false;
    if (this._animFrameId) {
      cancelAnimationFrame(this._animFrameId);
      this._animFrameId = null;
    }
  }

  _frame(now) {
    if (!this._running) return;
    const dt = Math.min(0.05, (now - this._lastTime) / 1000);
    this._lastTime = now;
    try {
      this._update(dt);
      this._render();
    } catch (err) {
      console.error('[UCG] MatchmakingBrawler frame error:', err);
    }
    this._animFrameId = requestAnimationFrame(this._frame);
  }

  _spawnFighter(team) {
    const pool = team === 'western' ? WESTERN_POOL : EASTERN_POOL;
    const unitType = pool[Math.floor(Math.random() * pool.length)];
    const cardData = getCardByUnitType(unitType);
    return {
      id: `brawl_${team}_${this._spawnCounter++}`,
      name: cardData.name,
      race: cardData.race,
      team,
      cardData,
      x: team === 'western' ? SPAWN_MARGIN : CANVAS_WIDTH - SPAWN_MARGIN,
      y: LANE_Y,
      radius: RADIUS,
      hp: cardData.hp,
      maxHp: cardData.hp,
      damage: cardData.damage,
      speed: (cardData.speed || 2.25) * 26, // walk-cycle cadence only, not actual movement
      attackSpeed: cardData.attackSpeed || 1.2,
      attackCooldown: 0,
      range: ENGAGE_DISTANCE,
      target: null,
      facingDir: team === 'western' ? 1 : -1,
      wobbleOffset: Math.random() * Math.PI * 2,
      hitFlashTimer: 0,
      isStructure: false,
      isHeld: false,
      isAir: false,
      isAttacking: false,
      age: 0,
      _attackPoseTimer: 0,
      _engaged: false
    };
  }

  _update(dt) {
    this._rendererStub.quality = window.tcgGame?.renderer?.quality || 'high';

    for (const team of ['western', 'eastern']) {
      const u = this.fighters[team];
      if (!u) {
        this._respawnTimer[team] -= dt;
        if (this._respawnTimer[team] <= 0) {
          this.fighters[team] = this._spawnFighter(team);
        }
        continue;
      }
      u.age += dt;
      if (u.hitFlashTimer > 0) u.hitFlashTimer = Math.max(0, u.hitFlashTimer - dt);
      if (u._attackPoseTimer > 0) {
        u._attackPoseTimer = Math.max(0, u._attackPoseTimer - dt);
        u.isAttacking = u._attackPoseTimer > 0;
      }
    }

    const w = this.fighters.western;
    const e = this.fighters.eastern;
    if (w && e) {
      w.target = e;
      e.target = w;
      const dist = e.x - w.x;
      if (dist > ENGAGE_DISTANCE) {
        w._engaged = false;
        e._engaged = false;
        w.x = Math.min(w.x + WALK_SPEED * dt, e.x - ENGAGE_DISTANCE);
        e.x = Math.max(e.x - WALK_SPEED * dt, w.x + ENGAGE_DISTANCE);
      } else {
        w._engaged = true;
        e._engaged = true;
        this._tickAttack(w, e, dt);
        this._tickAttack(e, w, dt);
      }
    } else if (w) {
      w.target = null;
      w._engaged = false;
    } else if (e) {
      e.target = null;
      e._engaged = false;
    }

    this.particles.update(dt);
  }

  _tickAttack(attacker, defender, dt) {
    if (attacker.hp <= 0 || defender.hp <= 0) return;
    attacker.attackCooldown -= dt;
    if (attacker.attackCooldown > 0) return;
    attacker.attackCooldown = attacker.attackSpeed;
    attacker._attackPoseTimer = ATTACK_POSE_DURATION;
    attacker.isAttacking = true;

    const color = TEAM_COLOR[attacker.team];
    const muzzleX = attacker.x + attacker.facingDir * attacker.radius * 0.8;
    const muzzleY = attacker.y - attacker.radius * 0.3;
    const hitY = defender.y - defender.radius * 0.3;
    this.particles.addMuzzleFlash(muzzleX, muzzleY, color, 5);
    this.particles.addTracer(muzzleX, muzzleY, defender.x, hitY, color, 0.08);
    this.particles.addHitSparks(defender.x, hitY, 'metal', 6, -attacker.facingDir);

    defender.hp = Math.max(0, defender.hp - attacker.damage);
    defender.hitFlashTimer = HIT_FLASH_DURATION;

    if (defender.hp <= 0) {
      this._kill(defender);
    }
  }

  _kill(unit) {
    this.particles.addUnitDeathShatter(unit);
    this.fighters[unit.team] = null;
    this._respawnTimer[unit.team] = RESPAWN_DELAY;
    this.kills++;
    this._updateKillBadge(true);
  }

  _render() {
    const ctx = this.ctx;
    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    ctx.strokeStyle = 'rgba(45, 76, 117, 0.5)';
    ctx.beginPath();
    ctx.moveTo(0, LANE_Y + RADIUS * 0.85);
    ctx.lineTo(CANVAS_WIDTH, LANE_Y + RADIUS * 0.85);
    ctx.stroke();

    this.particles.renderDecals(ctx);

    for (const team of ['western', 'eastern']) {
      const u = this.fighters[team];
      if (!u) continue;
      unitMixin.renderUnit.call(this._rendererStub, ctx, u, this._battlefieldStub);
    }

    this.particles.render(ctx, this._rendererStub.quality, VIEW_BOUNDS);
  }

  _updateKillBadge(pulse = false) {
    if (!this.killBadgeEl) return;
    this.killBadgeEl.textContent = `\u{1F480} KILLS: ${String(this.kills).padStart(2, '0')}`;
    if (pulse) {
      this.killBadgeEl.classList.remove('pulse');
      void this.killBadgeEl.offsetWidth;
      this.killBadgeEl.classList.add('pulse');
    }
  }
}
